import { stylize } from "@envestnet/envreact-component-library";
import React, { useEffect, useState } from "react";
import withIntl from "utils/withIntl";

import classnames from "classnames";

import PageLoadingComponent from "components/PageLoadingComponent";
import Tile from "components/Tile";
import { Application } from "types/Dashboard";
import { UserInfo, UserDetails } from "types/UserInfo";
import messages from "./messages";
import styles, { Styles } from "./styles";
import storage from "../../persistance";

interface InjectedProps extends Styles {
  /** Internal - injected from withIntl */
  formatMessage: FormatMessage;
  logout: () => Promise<boolean>;

  userInfo: UserInfo;
  userDetails: UserDetails;
  applications: Array<Application>;
  applicationListForRedirect: { [key: string]: Array<Application> };
  loadingApplications: boolean;
  noApplications: boolean;
  hasUpdatedTnCAccepted: boolean;
  tncForCidpUser: string | null;
  setDashboardLoading: (val: boolean) => void;
}

interface DefaultProps {}

export interface DashboardHomeLayoutProps extends DefaultProps {
  /** CSS class name to be applied to top level component */
  className?: string;

  /** Internal - injected from WithStyles */
  innerRef?: React.RefObject<HTMLDivElement>;

  /** Inline style object to be applied to top level component */
  style?: React.CSSProperties;

  removeToken: () => void;
  getApplications: () => void;
}

const defaultProps: DefaultProps = {};

type Props = InjectedProps & DashboardHomeLayoutProps;

const DashboardHomeLayout = (props: Props) => {
  const {
    classes,
    formatMessage,

    userInfo,
    getApplications,
    applications,
    applicationListForRedirect,
    loadingApplications,
    noApplications,
    setDashboardLoading,
  } = props;

  const [hideDashboard, setHideDashboard] = useState<boolean>(true);
  const hasGivenName = Boolean(userInfo?.given_name);
  const hasResourceParam = new URLSearchParams(window.location.search).has(
    "resource"
  );

  useEffect(() => {
    setDashboardLoading(hideDashboard);
  }, []);

  useEffect(() => {
    if ((userInfo as UserInfo).email !== "") {
      getApplications();
    }
  }, [userInfo]);

  const getRedirectUrl = (url: string) => {
    const { partnerId } = userInfo && userInfo.ext;
    if (!partnerId || !url) {
      return url;
    }

    if (url.includes("?")) {
      return `${url}&partnerId=${partnerId}`;
    }

    return `${url}?partnerId=${partnerId}`;
  };

  useEffect(() => {
    if (Object.keys(applicationListForRedirect).length > 0) {
      const resourceId = new URLSearchParams(window.location.search).get(
        "resource"
      );
      const [selectedPlatform, selectedApp] =
        (resourceId && resourceId.split("_")) || [];
      if (!selectedPlatform || !selectedApp) {
        setHideDashboard(false);
        return;
      }
      storage.removeItem("resourceId");

      const keys = Object.keys(applicationListForRedirect);
      if (keys.includes(selectedPlatform)) {
        const values = applicationListForRedirect[selectedPlatform];
        const foundApp = values.find((item) => item.id === selectedApp);
        if (foundApp && Object.prototype.hasOwnProperty.call(foundApp, "url")) {
          window.location.href = getRedirectUrl(foundApp.url || "");
        } else {
          setHideDashboard(false);
        }
      } else {
        setHideDashboard(false);
      }
    }
  }, [applicationListForRedirect]);

  useEffect(() => {
    setDashboardLoading(hideDashboard);
  }, [hideDashboard, setDashboardLoading]);

  const getTiles = () => {
    if (loadingApplications) {
      return (
        <div className={classes.loadingApplicationsSection}>
          <Tile tileName="loading" url="#" isLoading />
          <Tile tileName="loading" url="#" isLoading />
          <Tile tileName="loading" url="#" isLoading />
          <Tile tileName="loading" url="#" isLoading />
        </div>
      );
    }
    if (noApplications) {
      return (
        <div
          className={classnames(
            classes.noApplicationSection,
            "header-text-font-family"
          )}
        >
          <span>{formatMessage(messages.noAppsMessage)}</span>
        </div>
      );
    }

    return (
      <div className={classes.applicationSection}>
        {applications.map((app) => (
          <Tile
            key={app.id}
            url={getRedirectUrl(app.url ?? "")}
            tileName={app.name}
          />
        ))}
      </div>
    );
  };

  if (!hideDashboard) {
    return (
      <div className={classes.mainSectionParent}>
        <div className={classnames(classes.greeting, "header-font-family")}>
          {formatMessage(
            hasGivenName ? messages.greeting : messages.greetingWithoutComma
          )}
          {hasGivenName && (
            <>
              <br />
              {userInfo.given_name}
            </>
          )}
        </div>
        <div className={classes.tileSection}>
          {!noApplications && (
            <span className="header-font-family" style={{ marginLeft: "24px" }}>
              {formatMessage(messages.appTitleLabel)}
            </span>
          )}
          <div style={{ marginTop: "8px" }}>{getTiles()}</div>
        </div>
      </div>
    );
  }

  return hasResourceParam ? (
    <PageLoadingComponent isFullPage />
  ) : (
    <PageLoadingComponent />
  );
};

DashboardHomeLayout.defaultProps = defaultProps;

export default withIntl(
  stylize<Props, DefaultProps>(styles)(DashboardHomeLayout)
);
